import React, { useState, useEffect, useCallback } from "react";
import {
  Tag, Plus, RefreshCw, LayoutList, LayoutGrid,
} from "lucide-react";
import {
  API, apiFetch, useToast, ConfirmModal, Modal, Field, iCls, Toggle,
  SaveButton, PageHeader, TableCard, TableHead, TableSkeleton, StatusBadge,
  EditBtn, DeleteBtn, SearchBar, PrimaryBtn, EmptyState, getAdminSessionToken,
} from "./_shared";
import ImageEditModal from "../../components/admin/ImageEditModal";
import { usePermission } from "../../hooks/usePermission";
import { multipartUploadToS3, buildUniqueUploadKey, SINGLE_UPLOAD_MAX_BYTES } from "../../lib/multipartUpload";

const VIEW_KEY = "adminCategoriesViewMode";
const emptyForm = {
  name: "", slug: "", description: "", icon: "", iconImage: {}, isActive: true,
  seoTitle: "", seoDescription: "", seoKeywords: [], seoContent: "",
};

const StatPill = ({ label, value, tone }) => {
  const tones = {
    slate: "border-admin-border bg-admin-surface text-admin-text",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return (
    <div className={`rounded-2xl border-2 px-4 py-3 shadow-sm ${tones[tone] || tones.slate}`}>
      <p className="text-[10px] font-black uppercase tracking-wider text-admin-textMuted">{label}</p>
      <p className="mt-0.5 text-3xl font-black tabular-nums tracking-tight">{value}</p>
    </div>
  );
};

const AdminCategories = () => {
  const { push: toast, ToastPortal } = useToast();
  const { can } = usePermission();
  const canWrite = can("categories:write");

  const [categories, setCategories] = useState([]);
  const [locCountByName, setLocCountByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "card" ? "card" : "list";
    } catch {
      return "list";
    }
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingIconFile, setPendingIconFile] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const setter = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: val }));
  };

  const persistView = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_KEY, mode);
    } catch { /* noop */ }
  };

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/categories?withCounts=true`);
      const cats = Array.isArray(res) ? res : [];
      setCategories(cats);
      setLocCountByName(Object.fromEntries(cats.map((c) => [String(c._id), c.locationCount || 0])));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => {
    setForm(emptyForm);
    setModalMode("add");
    setEditId(null);
    setPendingIconFile(null);
    setModalOpen(true);
  };

  const openEdit = (cat) => {
    setForm({
      name: cat.name || "", slug: cat.slug || "", description: cat.description || "",
      icon: cat.icon || "", iconImage: cat.iconImage || {}, isActive: cat.isActive !== false,
      seoTitle: cat.seoTitle || "",
      seoDescription: cat.seoDescription || "",
      seoKeywords: Array.isArray(cat.seoKeywords) ? cat.seoKeywords : [],
      seoContent: cat.seoContent || "",
    });
    setModalMode("edit");
    setEditId(cat._id);
    setPendingIconFile(null);
    setModalOpen(true);
  };

  const uploadIconToS3 = async (file) => {
    const token = getAdminSessionToken();
    // Upload 1 request bị giới hạn ~4.5MB → tệp lớn đi multipart (key duy nhất); luôn dùng key/url BE trả về
    if (Number(file?.size || 0) > SINGLE_UPLOAD_MAX_BYTES) {
      const out = await multipartUploadToS3({
        apiBase: API,
        token,
        key: buildUniqueUploadKey("categories/icons/", file),
        file,
        contentType: file?.type || "application/octet-stream",
      });
      return { ...out, mimeType: file.type || "", size: file.size || 0 };
    }
    const fd = new FormData();
    fd.append("file", file);

    const res = await window.fetch(`${API}/media/upload?prefix=${encodeURIComponent("categories/icons/")}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Upload icon thất bại");
    return data?.data || data;
  };

  const handleConfirmIcon = async (processedFile) => {
    setUploadingIcon(true);
    try {
      const out = await uploadIconToS3(processedFile);
      setForm((p) => ({
        ...p,
        iconImage: {
          key: out.key,
          url: out.url,
          mimeType: out.mimeType || processedFile.type,
          size: out.size || processedFile.size,
          alt: "",
        },
        icon: "",
      }));
      toast("Đã upload icon", "success");
    } catch (e) {
      toast(e.message || "Upload icon thất bại", "error");
    } finally {
      setUploadingIcon(false);
      setPendingIconFile(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modalMode === "add") {
        await apiFetch(`${API}/categories`, { method: "POST", body: JSON.stringify(form) });
        toast("Thêm danh mục thành công!", "success");
      } else {
        await apiFetch(`${API}/categories/${editId}`, { method: "PUT", body: JSON.stringify(form) });
        toast("Cập nhật thành công!", "success");
      }
      setModalOpen(false);
      fetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // Danh mục còn địa điểm: bắt buộc chọn danh mục khác để chuyển sang rồi mới xoá
  const [reassign, setReassign] = useState({ target: "", busy: false });

  const handleDelete = async (id, reassignTo) => {
    try {
      const q = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : "";
      const res = await apiFetch(`${API}/categories/${id}${q}`, { method: "DELETE" });
      toast(res?.message || "Đã xoá danh mục!", "success");
      setConfirm(null);
      fetch();
    } catch (e) {
      toast(e.message, "error");
      if (e.code !== "CATEGORY_HAS_LOCATIONS") setConfirm(null);
    }
  };

  const activeCount = categories.filter((c) => c.isActive !== false).length;
  const inactiveCount = categories.length - activeCount;

  const filtered = categories.filter((c) => {
    const q = search.toLowerCase().trim();
    const matchQ = !q || c.name?.toLowerCase().includes(q) || (c.slug || "").toLowerCase().includes(q);
    const active = c.isActive !== false;
    const matchStatus =
      !filterStatus
      || (filterStatus === "active" && active)
      || (filterStatus === "inactive" && !active);
    return matchQ && matchStatus;
  });


  return (
    <div className="space-y-6 pb-8">
      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatPill label="Tổng danh mục" value={categories.length} tone="slate" />
        <StatPill label="Đang hiển thị" value={activeCount} tone="emerald" />
        <StatPill label="Đang ẩn" value={inactiveCount} tone="amber" />
      </div>

      <PageHeader
        title="Quản lý danh mục"
        subtitle={`${categories.length} danh mục · ${activeCount} hiển thị${filtered.length !== categories.length ? ` · Đang xem ${filtered.length} kết quả` : ""}`}
        action={
          <>
            <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm tên hoặc slug..." />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-xl border border-admin-border bg-admin-surface px-3 py-2.5 text-sm text-admin-text outline-none transition focus:border-admin-primary focus:ring-2 focus:ring-[rgba(37,106,191,0.18)] shadow-sm"
            >
              <option value="">Tất cả trạng thái</option>
              <option value="active">Đang hiển thị</option>
              <option value="inactive">Đang ẩn</option>
            </select>
            <div className="flex rounded-xl border border-admin-border bg-admin-muted p-0.5">
              <button
                type="button"
                title="Danh sách"
                onClick={() => persistView("list")}
                className={`flex h-9 w-9 items-center justify-center rounded-lg transition ${
                  viewMode === "list" ? "bg-white text-admin-primary shadow-sm" : "text-admin-textMuted hover:text-admin-text"
                }`}
              >
                <LayoutList size={17} />
              </button>
              <button
                type="button"
                title="Thẻ"
                onClick={() => persistView("card")}
                className={`flex h-9 w-9 items-center justify-center rounded-lg transition ${
                  viewMode === "card" ? "bg-white text-admin-primary shadow-sm" : "text-admin-textMuted hover:text-admin-text"
                }`}
              >
                <LayoutGrid size={17} />
              </button>
            </div>
            <button
              type="button"
              onClick={fetch}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-admin-border bg-admin-surface text-admin-textMuted shadow-sm transition hover:border-admin-primary/25 hover:bg-admin-primaryLight hover:text-admin-primary"
            >
              <RefreshCw size={15} />
            </button>
            {canWrite ? <PrimaryBtn onClick={openAdd} icon={Plus}>Thêm danh mục</PrimaryBtn> : null}
          </>
        }
      />

      <TableCard>
        {loading ? (
          viewMode === "list" ? (
            <TableSkeleton cols={8} rows={7} />
          ) : (
            <div className="grid animate-pulse grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-44 rounded-2xl bg-slate-100" />
              ))}
            </div>
          )
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Tag}
            title="Không có danh mục"
            description={search || filterStatus ? "Thử đổi bộ lọc hoặc từ khoá" : "Thêm danh mục đầu tiên"}
          />
        ) : viewMode === "list" ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <TableHead cols={["Danh mục", "Slug", "Mô tả", "Địa điểm", "Trạng thái", "Ngày tạo", ""]} />
              <tbody className="divide-y divide-admin-border">
                {filtered.map((cat) => {
                  const locN = locCountByName[String(cat._id)] ?? 0;
                  const iconUrl = cat.iconImage?.url;
                  return (
                    <tr key={cat._id} className="transition-colors hover:bg-admin-muted/60">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white shadow-md ring-2 ring-white"
                            style={{ background: "#6366f1" }}
                          >
                            {iconUrl ? (
                              <img src={iconUrl} alt="" className="h-7 w-7 object-contain" />
                            ) : (
                              cat.icon || cat.name?.[0]?.toUpperCase() || "?"
                            )}
                          </div>
                          <span className="font-semibold text-slate-800">{cat.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-admin-textMuted">{cat.slug || "—"}</td>
                      <td className="max-w-[200px] truncate px-5 py-4 text-admin-textMuted">{cat.description || "—"}</td>
                      <td className="px-5 py-4">
                        <span className="inline-flex rounded-lg bg-admin-primaryLight px-2 py-1 text-xs font-bold tabular-nums text-admin-primary ring-1 ring-admin-primaryLight">
                          {locN}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge active={cat.isActive !== false} />
                      </td>
                      <td className="px-5 py-4 text-xs text-admin-textMuted">
                        {cat.createdAt ? new Date(cat.createdAt).toLocaleDateString("vi-VN") : "—"}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {canWrite ? <EditBtn variant="solid" onClick={() => openEdit(cat)} /> : null}
                          {canWrite ? <DeleteBtn variant="solid" onClick={() => setConfirm(cat._id)} /> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((cat) => {
              const locN = locCountByName[String(cat._id)] ?? 0;
              const active = cat.isActive !== false;
              const iconUrl = cat.iconImage?.url;
              return (
                <div
                  key={cat._id}
                  className="group relative flex flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-sm ring-1 ring-black/[0.02] transition hover:-translate-y-0.5 hover:border-admin-primary/25/60 hover:shadow-lg hover:shadow-admin-primary/5"
                >
                  <div className="h-1.5 w-full bg-admin-primary" aria-hidden />
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div
                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-xl font-bold text-white shadow-md"
                        style={{ background: "#6366f1" }}
                      >
                        {iconUrl ? (
                          <img src={iconUrl} alt="" className="h-8 w-8 object-contain" />
                        ) : (
                          cat.icon || cat.name?.[0]?.toUpperCase() || "?"
                        )}
                      </div>
                      <StatusBadge active={active} />
                    </div>
                    <h3 className="mt-3 line-clamp-1 text-base font-bold text-admin-text">{cat.name}</h3>
                    <p className="mt-0.5 font-mono text-[11px] text-admin-textMuted">{cat.slug || "—"}</p>
                    <p className="mt-2 line-clamp-2 flex-1 text-sm leading-snug text-admin-textMuted">
                      {cat.description || "Chưa có mô tả."}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="rounded-lg bg-admin-primaryLight px-2 py-1 text-[11px] font-bold text-admin-primary ring-1 ring-admin-primaryLight">
                        {locN} địa điểm
                      </span>
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-admin-border pt-3">
                      <span className="text-[11px] text-admin-textMuted">
                        {cat.createdAt ? new Date(cat.createdAt).toLocaleDateString("vi-VN") : "—"}
                      </span>
                      <div className="flex gap-1.5">
                        {canWrite ? <EditBtn variant="solid" onClick={() => openEdit(cat)} /> : null}
                        {canWrite ? <DeleteBtn variant="solid" onClick={() => setConfirm(cat._id)} /> : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </TableCard>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === "add" ? "Thêm danh mục" : "Sửa danh mục"}
        maxWidthClass="max-w-2xl"
        width="max-w-lg"
      >
        <form onSubmit={handleSave} className="flex flex-col gap-0">
          <div className="space-y-4">
            <Field label="Tên danh mục" required>
              <input className={iCls} value={form.name} onChange={setter("name")} placeholder="Nhập tên danh mục" required />
            </Field>
            <Field label="Slug">
              <input className={iCls} value={form.slug} onChange={setter("slug")} placeholder="tu-dong-tao-neu-bo-trong" />
            </Field>
            <Field label="Mô tả">
              <textarea className={iCls} rows={3} value={form.description} onChange={setter("description")} placeholder="Mô tả danh mục..." />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Icon (.png)">
                <div className="flex items-center gap-2">
                  <label
                    className={`inline-flex h-10 cursor-pointer items-center justify-center rounded-xl px-3 text-sm font-semibold shadow-sm transition ${
                      uploadingIcon
                        ? "cursor-not-allowed bg-admin-muted text-admin-textMuted"
                        : "border border-admin-border bg-admin-surface text-admin-text hover:bg-admin-muted"
                    }`}
                    title="Chọn ảnh icon"
                  >
                    {uploadingIcon ? "Đang upload..." : "Chọn ảnh"}
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      disabled={uploadingIcon}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) setPendingIconFile(f);
                        e.target.value = "";
                      }}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, iconImage: {}, icon: "" }))}
                    className="inline-flex h-10 items-center justify-center rounded-xl border border-admin-border bg-admin-surface px-3 text-sm font-semibold text-admin-text shadow-sm transition hover:bg-admin-muted"
                    title="Gỡ icon"
                  >
                    Gỡ
                  </button>

                  <div className="flex flex-1 items-center justify-end">
                    <div
                      className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-admin-border bg-admin-surface shadow-sm"
                      title="Preview icon"
                    >
                      {form.iconImage?.url ? (
                        <img src={form.iconImage.url} alt="" className="h-7 w-7 object-contain" />
                      ) : (
                        <span className="text-xs font-semibold text-slate-400">—</span>
                      )}
                    </div>
                  </div>
                </div>
              </Field>
            </div>

            <div
              className="flex h-24 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/50"
              style={{ background: `#6366f112` }}
            >
              <div
                className="flex h-14 w-14 items-center justify-center rounded-2xl text-2xl font-bold text-white shadow-lg ring-4 ring-white/80"
                style={{ background: "#6366f1" }}
              >
                {form.iconImage?.url ? (
                  <img src={form.iconImage.url} alt="" className="h-9 w-9 object-contain" />
                ) : (
                  form.icon || form.name?.[0]?.toUpperCase() || "?"
                )}
              </div>
            </div>

            <Toggle checked={form.isActive} onChange={setter("isActive")} />

            <div className="pt-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">SEO</p>
              <div className="mt-3 space-y-4">
                <Field label="SEO Title">
                  <input className={iCls} value={form.seoTitle} onChange={setter("seoTitle")} placeholder="Title SEO danh mục..." />
                </Field>
                <Field label="SEO Description">
                  <textarea className={iCls} rows={2} value={form.seoDescription} onChange={setter("seoDescription")} placeholder="Mô tả SEO..." />
                </Field>
                <Field label="SEO Keywords (phân tách bằng dấu phẩy)">
                  <input
                    className={iCls}
                    value={(form.seoKeywords || []).join(", ")}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      seoKeywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    }))}
                    placeholder="quán cafe đà lạt, cafe đẹp..."
                  />
                </Field>
                <Field label="SEO Content">
                  <textarea className={iCls} rows={4} value={form.seoContent} onChange={setter("seoContent")} placeholder="Nội dung SEO dài..." />
                </Field>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-3 border-t border-admin-border pt-4">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="flex-1 rounded-xl border border-admin-border bg-admin-surface py-3 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted"
            >
              Huỷ bỏ
            </button>
            <SaveButton saving={saving} isEdit={modalMode === "edit"} />
          </div>
        </form>
      </Modal>

      {confirm && (() => {
        const cat = categories.find((c) => c._id === confirm);
        const n = cat?.locationCount || 0;
        if (n === 0) {
          return (
            <ConfirmModal
              message={`Xoá danh mục "${cat?.name || ""}"?`}
              description="Danh mục không có địa điểm nào. Hành động này không thể hoàn tác."
              onConfirm={() => handleDelete(confirm)}
              onCancel={() => setConfirm(null)}
            />
          );
        }
        const others = categories.filter((c) => c._id !== confirm);
        return (
          <Modal
            open
            onClose={() => { setConfirm(null); setReassign({ target: "", busy: false }); }}
            title={`Không thể xoá ngay: "${cat?.name}" còn ${n} địa điểm`}
            maxWidthClass="max-w-md"
            footer={
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setConfirm(null); setReassign({ target: "", busy: false }); }} className="rounded-lg border border-admin-border bg-admin-surface px-4 py-2 text-sm font-semibold text-admin-text hover:bg-admin-muted">
                  Huỷ
                </button>
                <button
                  type="button"
                  disabled={!reassign.target || reassign.busy}
                  onClick={async () => {
                    setReassign((r) => ({ ...r, busy: true }));
                    await handleDelete(confirm, reassign.target);
                    setReassign({ target: "", busy: false });
                  }}
                  className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  Chuyển {n} địa điểm & xoá danh mục
                </button>
              </div>
            }
          >
            <p className="text-sm text-admin-text">
              Xoá danh mục khi còn địa điểm sẽ làm các địa điểm đó mất phân loại. Hãy chọn danh mục để chuyển các địa điểm sang:
            </p>
            {others.length === 0 ? (
              <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Đây là danh mục duy nhất. Hãy tạo thêm một danh mục khác trước.
              </p>
            ) : (
              <select value={reassign.target} onChange={(e) => setReassign((r) => ({ ...r, target: e.target.value }))} className={iCls + " mt-3"}>
                <option value="">— Chọn danh mục đích —</option>
                {others.map((c) => (
                  <option key={c._id} value={c._id}>{c.name} ({c.locationCount || 0} địa điểm)</option>
                ))}
              </select>
            )}
          </Modal>
        );
      })()}
      <ToastPortal />

      {pendingIconFile && (
        <ImageEditModal
          title="Chỉnh icon danh mục"
          file={pendingIconFile}
          shape="round"
          onCancel={() => setPendingIconFile(null)}
          onConfirm={handleConfirmIcon}
        />
      )}
    </div>
  );
};

export default AdminCategories;
